import { useState } from "react";
import { Container } from "react-bootstrap";
import { CollapseRow, Control211, HorizontalDrawer } from "../controls";
import { useBeFigure } from "./BeFigureContextProvider";
import { FigureDataRows } from "./FigureDataRows";
import { FigureVars } from "./FigureVars";
import {
  ADD_DATA_GROUP,
  ADD_REFERENCE_LINE,
  ADD_REGRESSION_LINE,
  SET_FIGURE_TYPE,
  SET_LEGEND_TYPE,
} from "./functions/interface";
import { FigureVarSettingsDto, FigureVarTypeDto } from "./types";

export interface BeFigureOptionsProps {
  show: boolean;
}

export const defaultDataGroupVarSettings: FigureVarSettingsDto[] = [
  { varName: "order", label: "Order", type: FigureVarTypeDto.number },
  { varName: "orderHeader", label: "Label", type: FigureVarTypeDto.string },
  { varName: "seed", label: "Seed", type: FigureVarTypeDto.number },
  { varName: "length", label: "Length", type: FigureVarTypeDto.number },
];

/**
 * Container to show the back end figure options
 *
 * @param props - Destructured parameter object
 * @param props.show - Whether the options panel is visible
 * @returns A React element containing the options panel for the back end figure, or null if figure settings are not available
 */
export const BeFigureOptions = ({ show }: BeFigureOptionsProps): React.ReactElement | null => {
  const { state, dispatch } = useBeFigure();
  const figureInput = state?.figureInput;
  const figureSettings = state?.figureSettings;
  const figureTypes =
    state?.figureSettings.figureTypeSettings.map((ft) => ({
      label: ft.label,
      value: ft.value,
    })) ?? [];
  const figureTypeProperties =
    state?.figureSettings.figureTypeSettings.find((ft) => ft.value === figureInput?.figureType)
      ?.properties ?? [];
  const allowedRegressionTypes =
    state?.figureSettings.figureTypeSettings.find((ft) => ft.value === figureInput?.figureType)
      ?.allowedRegressionTypes ?? [];
  const isLegendVisible =
    state?.figureSettings.figureTypeSettings.find((ft) => ft.value === figureInput?.figureType)
      ?.isLegendVisible ?? true;

  const legendTypes = state?.figureSettings.figureLegendSettings.map((lt) => lt.legendType) ?? [];
  const [showImageProps, setShowImageProps] = useState<boolean>(false);
  const [showTypeProps, setShowTypeProps] = useState<boolean>(false);

  return !figureSettings || !figureTypes || !figureInput ? null : (
    <HorizontalDrawer
      show={show}
      side="right"
    >
      <Container className="border rounded border-gray-500 border-2 ps-3 pe-4 me-3 pb-1">
        <Control211
          label="Figure type"
          labelWidth={3}
          value={figureInput.figureType}
          options={figureTypes}
          setValue={(newValue) =>
            dispatch({
              operation: SET_FIGURE_TYPE,
              newValue,
            })
          }
          id={"figure-type"}
        />
        <Control211
          label="Legend type"
          labelWidth={3}
          value={figureInput.legendType}
          options={legendTypes}
          setValue={(newValue) =>
            dispatch({
              operation: SET_LEGEND_TYPE,
              newValue,
            })
          }
          id={"legend-type"}
        />
        {figureSettings.figureProperties.length > 0 && (
          <CollapseRow
            label="Image properties"
            isOpen={showImageProps}
            setIsOpen={setShowImageProps}
            className="border rounded border-gray-500 border-2 px-1"
          >
            <FigureVars
              id={"properties"}
              dataPath={"/properties"}
              varSettings={figureSettings.figureProperties}
              properties={figureInput.properties}
            />
          </CollapseRow>
        )}
        {figureTypeProperties.length > 0 && (
          <>
            <hr />
            <CollapseRow
              label="Figure properties"
              isOpen={showTypeProps}
              setIsOpen={setShowTypeProps}
              className="border rounded border-gray-500 border-2 px-1"
            >
              <FigureVars
                id={"figure-properties"}
                dataPath={"/figureTypeProperties"}
                varSettings={figureTypeProperties}
                properties={figureInput.figureTypeProperties}
              />
            </CollapseRow>
          </>
        )}
        <hr />
        <FigureDataRows
          dataPath={"/axes"}
          itemLabel="Axis"
          label="Axes"
          noAddRemove
          propertiesKey="properties"
        />
        <hr />
        <FigureDataRows
          addType={ADD_DATA_GROUP}
          dataPath={"/dataGroups"}
          itemLabel="Data series"
          label="Data Groups"
          propertiesKey="properties"
        />
        {isLegendVisible && (
          <>
            <hr />
            <FigureDataRows
              dataPath={"/legend"}
              itemLabel="Settings"
              label="Legend"
              propertiesKey="properties"
              noAddRemove
              noNumber
            />
          </>
        )}
        {allowedRegressionTypes.length > 0 && (
          <>
            <hr />
            <FigureDataRows
              addType={ADD_REGRESSION_LINE}
              dataPath={"/regressionLines"}
              itemLabel="Regression line"
              label="Regression Lines"
              propertiesKey="properties"
            />
          </>
        )}
        <hr />
        <FigureDataRows
          addType={ADD_REFERENCE_LINE}
          dataPath={"/referenceLines"}
          itemLabel="Reference line"
          label="Reference Lines"
          propertiesKey="properties"
        />
      </Container>
    </HorizontalDrawer>
  );
};

BeFigureOptions.displayName = "BeFigureOptions";
